import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import styles from 'components/medicalHistory/page.module.scss';
import Stack from '@mui/material/Stack';
import Button from 'components/common/button';
import { StateTypeLocalStorage, usePersistMHReducer } from 'reducer/medicalHistory';
import SearchableTextBoxMedications from 'components/common/searchableTextBoxMedications';
import { OptionTypeForMedications } from 'components/medicalHistory/types/types';
import MetaTag from 'components/meta';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { NullablePatientDTO } from 'types/patients';
import Alert, { AlertVariant } from 'components/common/Alerts';
import isNil from 'lodash/isNil';
import AlertTitle from '@mui/material/AlertTitle';

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function MedicationsScreen({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;

  const [medications, setMedications] = useState<OptionTypeForMedications[]>(
    [],
  );

  const [error, setError] = useState('');
  const [state, dispatch] = usePersistMHReducer();

  const clickNextOrSubmit = async (nextPage: number) => {
    const findOther = medications.find((item) => item.value === 'Others' && item.text === '');
    if (findOther) {
      setError('Please input others medicine name.');
      return;
    }
    await dispatch({
      type: actionPage,
      name: 'Medications',
      value: medications,
    });
    nextOrSubmitButton(nextPage);
  };

  const getMedicationsItem = (items: OptionTypeForMedications[]) => {
    setMedications(items);
    setError('');
  };

  useEffect(() => {
    if (state) {
      const medications = state.find((p: StateTypeLocalStorage) => p.name === actionPage);
      if (medications?.value?.length) {
        setMedications(medications.value);
      } else {
        const mappingData = patient?.medications?.map((item) => (
          {
            ...item,
            value: item.displayName ?? '',
            label: item.displayName ?? '',
            readOnly: true,
          }
        ));
        if (mappingData) {
          setMedications(mappingData);
        }
      }
    }
  }, []);

  const hasDosespotError = useMemo(() => !isNil(patient?.dosespotError), [patient]);

  return (
    <>
      <MetaTag title="Medications" />
      <Box pb={3.5} mb={4}>
        <Box>
          <h2 className={`marginTopZero ${styles.titleText}`}>Medications</h2>
          <p className={styles.titleTextSubText}>
            Which medications are you currently taking?
          </p>
          {error && <p className="error-message">{error}</p>}
          <Alert
            severity={AlertVariant.ERROR}
            open={hasDosespotError}
            className="mb-3 !w-full"
          >
            <AlertTitle
              className="!text-sm"
            >
              {patient?.dosespotError?.title ?? ''}
            </AlertTitle>
            <p className="text-xs">
              {patient?.dosespotError?.details ?? ''}
            </p>
          </Alert>
          <Stack className="mt-2">
            <SearchableTextBoxMedications
              id="medications-history"
              placeholder="Search for type of medication"
              onChange={getMedicationsItem}
              value={medications}
              disabled={hasDosespotError}
            />
            <p className={styles.separator} />
          </Stack>
        </Box>
        <div className="next-button-fixed">
          <Button
            onClick={() => clickNextOrSubmit(nextPage)}
          >
            Next
          </Button>
        </div>
      </Box>
    </>
  );
}

export default MedicationsScreen;
